/**
 * §7 — attaching a receipt, from wherever the operator happens to be.
 *
 * WHY THIS IS ONE COMPONENT
 *
 * Two screens attach receipts now: the editor, and the Entries list so that
 * entries needing one can be cleared before the rest are posted in bulk. The
 * upload is four steps -- presign, PUT the bytes straight to the bucket,
 * record the row, then deal with what failed -- and a second copy of that is
 * a second place for the drag handlers to forget `preventDefault`, or for the
 * file input to forget to clear itself.
 *
 * The drag handlers and the input now live in FileDropzone (2026-09-13), shared
 * with bank statements on the Reconcile screen. This keeps the receipt half:
 * which entry, and the receipt endpoints.
 *
 * THE TARGET IS RESOLVED, NOT PASSED -- AND ONCE PER BATCH
 *
 * `onResolveTarget` rather than a header id, because the editor's answer is
 * not always known in advance: a NEW entry has no header, so attaching to it
 * has to create it first. The queue simply returns the row's id. Both hand
 * back the id the upload should go to, or null to abandon quietly.
 *
 * Several files can arrive in one drop (2026-09-14), and the target is
 * resolved ONCE for all of them. Resolved per file, three receipts dropped on
 * a new entry would create three entries.
 *
 * ONE FILE FAILING DOES NOT STOP THE REST. The files go up one after another;
 * a refused file is named in the error and the others still attach. Losing
 * two good receipts because a third was a .docx would be the zone punishing
 * the operator for the one thing it already reports.
 *
 * WHAT IT DOES NOT OWN
 *
 * The list of receipts, and what to say afterwards. The editor keeps them in
 * its own state and the queue refetches; a component that owned the list would
 * have to know how each screen refreshes, which is the thing they genuinely do
 * differently.
 */

import { api, ApiError, uploadToStorage, type Attachment } from '../api.js';
import { FileDropzone } from './FileDropzone.js';

export interface ReceiptDropzoneProps {
  /**
   * The header to attach to, resolved at the moment of upload -- once for a
   * whole drop. Null abandons without an error; the caller has already said why.
   */
  onResolveTarget: () => Promise<string | null> | string;
  /**
   * Once per drop or pick, with every file that attached, in order. Not called
   * when none did.
   */
  onAttached: (attachments: Attachment[], headerId: string) => void;
  onError: (message: string) => void;
  /** A shorter zone for the queue's detail pane, which has less room. */
  compact?: boolean;
}

/**
 * Upload one receipt to an entry: presign, POST the bytes to storage, record.
 *
 * Exported so the Entries rows, which accept dropped files directly, use this
 * sequence rather than a second copy of it. The row is written only after the
 * bytes are up -- see the comment in the component below.
 */
export async function uploadReceipt(headerId: string, file: File): Promise<Attachment> {
  const body = { filename: file.name, contentType: file.type, sizeBytes: file.size };
  const { upload: presigned } = await api.presignAttachment(headerId, body);
  await uploadToStorage(presigned, file);
  const { attachment } = await api.recordAttachment(headerId, { ...body, key: presigned.key });
  return attachment;
}

/** "a.pdf", "a.pdf and b.pdf", "a.pdf, b.pdf and c.pdf" -- for the notice. */
export function describeAttached(attachments: Attachment[]): string {
  const names = attachments.map((attachment) => attachment.originalFilename);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The sentence for files that did not attach. One file alone keeps the plain
 * reason, exactly as before several were possible; in a batch each is named.
 */
export function describeRefused(refused: Array<{ name: string; reason: string }>, total: number): string {
  if (total === 1 && refused.length === 1) return refused[0]!.reason;
  const each = refused.map((one) => `${one.name}: ${one.reason}`).join(' ');
  return `${refused.length} of ${total} files were not attached. ${each}`;
}

export function reasonFor(caught: unknown): string {
  return caught instanceof ApiError ? caught.message : 'Could not attach that file.';
}

export function ReceiptDropzone({
  onResolveTarget,
  onAttached,
  onError,
  compact,
}: ReceiptDropzoneProps) {
  async function upload(files: File[]) {
    const target = await onResolveTarget();
    if (target === null) return;

    const attached: Attachment[] = [];
    const refused: Array<{ name: string; reason: string }> = [];
    for (const file of files) {
      /*
       * The row is written only after the bytes are up. §9.8 is explicit that
       * the order matters in this direction: an object nobody points at costs
       * storage and nothing else, while a row pointing at an object that is
       * not there is a receipt that lists and then fails to open, which reads
       * as data loss.
       */
      try {
        attached.push(await uploadReceipt(target, file));
      } catch (caught) {
        refused.push({ name: file.name, reason: reasonFor(caught) });
      }
    }

    if (attached.length > 0) onAttached(attached, target);
    if (refused.length > 0) onError(describeRefused(refused, files.length));
  }

  return (
    <FileDropzone
      upload={upload}
      onError={onError}
      inputLabel="Attach a receipt"
      idleText="Drop receipts here, or choose files"
      note="PDFs or images, up to 20 MB each"
      compact={compact}
      multiple
    />
  );
}
